// libs
import { produce } from 'immer';
import { ActionTypes, Action } from './types';

// constants
import {
  DEFAULT_PRODUCT_INFO,
  DEFAULT_WIDGET_INFO,
  HYBRID_PDP_WIDGETS,
  RECO_WIDGET_TYPE,
} from './constants';

// defs
import { HybridPDPState, HybridPDPWidgetState } from './types';

// helpers
import { transformProductInfo, transformWidgetInfo } from './helpers';

const getDefaultWidgetState = (widgetType = RECO_WIDGET_TYPE): HybridPDPWidgetState => ({
  fetching: false,
  statusCode: '',
  type: widgetType,
  details: { ...DEFAULT_WIDGET_INFO, widgetType },
});

const getDefaultWidgetsByType = () =>
  HYBRID_PDP_WIDGETS.reduce((acc, widget) => {
    acc[widget.type] = getDefaultWidgetState(widget.type);
    return acc;
  }, {} as { [key: string]: HybridPDPWidgetState });

const initialState: Readonly<HybridPDPState> = Object.freeze({
  productID: '',
  product: { fetching: false, statusCode: '', details: DEFAULT_PRODUCT_INFO },
  widgets: getDefaultWidgetState(RECO_WIDGET_TYPE),
  widgetsByType: getDefaultWidgetsByType(),
});

const getWidgetPayload = (payload: any = {}) => {
  const hasDataPayload = payload && Object.prototype.hasOwnProperty.call(payload, 'data');
  const data = hasDataPayload ? payload.data : payload;
  const widgetType = payload?.widgetType || data?.widget_type || RECO_WIDGET_TYPE;
  const statusCode = payload?.statusCode || payload;

  return { data, statusCode, widgetType };
};

const getCurrentWidgetState = (draft: HybridPDPState, widgetType: string) =>
  draft.widgetsByType?.[widgetType] ||
  (widgetType === RECO_WIDGET_TYPE ? draft.widgets : undefined) ||
  getDefaultWidgetState(widgetType);

const setWidgetState = (
  draft: HybridPDPState,
  widgetType: string,
  widgetState: HybridPDPWidgetState
) => {
  if (!draft.widgetsByType) {
    draft.widgetsByType = getDefaultWidgetsByType();
  }

  draft.widgetsByType[widgetType] = widgetState;

  if (widgetType === RECO_WIDGET_TYPE) {
    draft.widgets = widgetState;
  }
};

export default (state: HybridPDPState = initialState, action: Action): HybridPDPState =>
  produce(state, (draft) => {
    const { type, payload } = action;

    switch (type) {
      case ActionTypes.FETCH_DETAILS_PROGRESS: {
        draft.product = { ...initialState.product, fetching: true };
        return;
      }

      case ActionTypes.FETCH_DETAILS_SUCCESS: {
        draft.productID = payload.id;
        draft.product = {
          ...initialState.product,
          details: transformProductInfo(payload),
          fetching: false,
          statusCode: '200',
        };
        return;
      }

      case ActionTypes.FETCH_DETAILS_FAILURE: {
        draft.product = { ...initialState.product, fetching: false, statusCode: payload };
        return;
      }

      case ActionTypes.FETCH_WIDGETS_PROGRESS: {
        const { widgetType } = getWidgetPayload(payload);
        setWidgetState(draft, widgetType, { ...getDefaultWidgetState(widgetType), fetching: true });
        return;
      }

      case ActionTypes.FETCH_WIDGETS_SUCCESS: {
        const { data, widgetType } = getWidgetPayload(payload);
        const details = transformWidgetInfo(data);

        setWidgetState(draft, widgetType, {
          ...getDefaultWidgetState(widgetType),
          ...(payload?.recoveredOnClient === true ? { recoveredOnClient: true } : {}),
          fetching: false,
          statusCode: '200',
          details: {
            ...details,
            widgetType: details.widgetType || widgetType,
          },
        });
        return;
      }

      case ActionTypes.FETCH_WIDGETS_FAILURE: {
        const { statusCode, widgetType } = getWidgetPayload(payload);
        setWidgetState(draft, widgetType, {
          ...getDefaultWidgetState(widgetType),
          fetching: false,
          statusCode: String(statusCode || ''),
        });
        return;
      }

      case ActionTypes.UPDATE_WIDGETS: {
        const { data, widgetType } = getWidgetPayload(payload);
        const currentWidget = getCurrentWidgetState(draft, widgetType);
        const currentDetails = currentWidget.details || DEFAULT_WIDGET_INFO;
        const nextPageNumber = currentDetails.pageNumber + 1;
        const nextDetails = transformWidgetInfo(data, nextPageNumber);

        setWidgetState(draft, widgetType, {
          ...currentWidget,
          fetching: false,
          statusCode: '200',
          details: {
            ...currentDetails,
            ...nextDetails,
            widgetType: nextDetails.widgetType || widgetType,
            products: [...(currentDetails.products || []), ...(nextDetails.products || [])],
            pageNumber: nextPageNumber,
          },
        });
      }
    }
  });
